import { useRef } from 'react';
import equals from 'ramda/es/equals';

/**
 * A custom hook that returns a memoized array of mapped values, ensuring that
 * the returned array only updates when the mapped values change. This is useful
 * for optimizing performance by avoiding unnecessary re-renders when the mapped
 * values remain the same.
 *
 * @template T - The type of the elements in the input array.
 * @template S - The type of the elements in the output array after mapping.
 *
 * @param {Array<T>} value - The input array of values to be mapped.
 * @param {(arg1: T) => S} [map=(value: T) => value] - A mapping function that
 * transforms each element of the input array into a new value. Defaults to an
 * identity function if not provided.
 *
 * @returns {Array<S>} A memoized array of mapped values that only updates when
 * the mapped values change.
 */
export const useEqualValue = <T, S = T>(
  value: Array<T>,
  map: (arg1: T) => S = (value) => value as unknown as S
): Array<S> => {
  const prevValue = useRef<Array<S>>([]);
  const newValue = value.map(map);

  if (!equals(prevValue.current, newValue)) {
    prevValue.current = newValue;
  }

  return prevValue.current;
};

export const useStableValue = <T>(value: T, comparator: (arg1: T, arg2: T) => boolean): T => {
  const prevValue = useRef<T>(value);

  if (!comparator(prevValue.current, value)) {
    prevValue.current = value;
  }

  return prevValue.current;
};
